import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  Container,
  Badge,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Verified,
  Comment,
  Business,
  LocalHospital,
  Phone,
  Email,
  Language,
  Star,
  Edit,
  Visibility,
  People,
  Schedule,
  MessageOutlined,
  CheckCircle,
  RadioButtonUnchecked,
  TrendingUp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import './bussinessCenter.css'

const BusinessCenter = () => {
  const { profilePicture } = useContext(UserContext);
  const [employeeData, setEmployeeData] = useState(null);
  const [doctorRatings, setDoctorRatings] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clinicsLoading, setClinicsLoading] = useState(true);
  const [completionData, setCompletionData] = useState({
    percentage: 0,
    completedItems: [],
    totalItems: 6
  });

  // Separate error states
  const [employeeError, setEmployeeError] = useState(null);
  const [ratingsError, setRatingsError] = useState(null);
  const [clinicsError, setClinicsError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const userId = sessionStorage.getItem('userId');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Reset errors
      setEmployeeError(null);
      setRatingsError(null);
      setClinicsError(null);

      try {
        // Fetch employee data (critical)
        const employeeResponse = await fetch(`${backendUrl}/employee/employee/${userId}`);
        if (!employeeResponse.ok) throw new Error('Failed to fetch employee data');
        const employeeData = await employeeResponse.json();

        if (employeeData.isSuccess) {
          setEmployeeData(employeeData.data);

          // Fetch associated clinics if they exist (non-critical)
          if (employeeData.data.associatedClinics && employeeData.data.associatedClinics.length > 0) {
            fetchAssociatedClinics(employeeData.data.associatedClinics);
          } else {
            setClinicsLoading(false);
          }
        } else {
          throw new Error(employeeData.error || 'Failed to load employee data');
        }
      } catch (err) {
        console.error('Employee data error:', err);
        setEmployeeError(err.message);
      }

      // Fetch doctor ratings (non-critical) - separate try-catch
      try {
        const ratingsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL_WEB}/DoctorRating/list-by-doctor/${userId}`);
        if (!ratingsResponse.ok) throw new Error('Failed to fetch doctor ratings');
        const ratingsData = await ratingsResponse.json();

        if (ratingsData.isSuccess) {
          setDoctorRatings(ratingsData.data);
        } else {
          throw new Error('No ratings data available');
        }
      } catch (err) {
        console.error('Ratings error:', err);
        setRatingsError(err.message);
        // Don't break the entire component - just log and continue
      }

      setLoading(false);
    };

    const fetchAssociatedClinics = async (clinicIds) => {
      try {
        setClinicsLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/healthcareprovider/list/1/100`);

        if (!response.ok) throw new Error('Failed to fetch clinics');

        const data = await response.json();
        if (data.resultList) {
          // Filter clinics to only show the associated ones
          const associatedClinics = data.resultList.filter(clinic =>
            clinicIds.includes(clinic.id)
          );
          setClinics(associatedClinics);
        }
      } catch (err) {
        console.error('Clinics error:', err);
        setClinicsError(err.message);
      } finally {
        setClinicsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Calculate completion percentage
    const calculateCompletion = () => {
      const completionFields = [
        { key: 'isAssociationCompleted', label: 'Professional Associations' },
        { key: 'isAwardsCompleted', label: 'Awards & Recognition' },
        { key: 'isCertificateCompleted', label: 'Certificates' },
        { key: 'isEducationCompleted', label: 'Education' },
        { key: 'isLicenceCompleted', label: 'Licenses' },
        { key: 'isWorkExperienceCompleted', label: 'Work Experience' }
      ];

      const completedItems = completionFields.filter(field => {
        const value = sessionStorage.getItem(field.key);
        return value === 'true';
      });

      const percentage = Math.round((completedItems.length / completionFields.length) * 100);

      setCompletionData({
        percentage,
        completedItems: completedItems.map(item => item.label),
        totalItems: completionFields.length,
        allItems: completionFields.map(field => ({
          ...field,
          completed: sessionStorage.getItem(field.key) === 'true'
        }))
      });
    };

    calculateCompletion();

    // Listen for profile completion updates
    const handleProfileUpdate = () => {
      calculateCompletion();
    };

    window.addEventListener('profileCompletionUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileCompletionUpdated', handleProfileUpdate);
    };
  }, [employeeData]);

  // Calculate stats from ratings with error handling
  const calculateStats = () => {
    if (!doctorRatings.length || ratingsError) {
      return {
        totalConsultations: 0,
        averageRating: 0,
        totalReviews: 0,
        activeClinic: clinics.length || 1
      };
    }

    const totalRating = doctorRatings.reduce((sum, rating) => sum + rating.rateAverage, 0);
    const averageRating = (totalRating / doctorRatings.length).toFixed(1);

    return {
      totalConsultations: doctorRatings.length,
      averageRating: averageRating,
      totalReviews: doctorRatings.length,
      activeClinic: clinics.length || 1
    };
  };

  const stats = calculateStats();

  const statsCards = [
    { value: stats.totalConsultations.toString(), label: 'Total Consultations', icon: <People />, accent: false },
    { value: stats.averageRating.toString(), label: 'Average Rating', icon: <Star />, accent: true },
    { value: stats.totalReviews.toString(), label: 'Patient Reviews', icon: <MessageOutlined />, accent: false },
    { value: stats.activeClinic.toString(), label: 'Active Clinics', icon: <LocalHospital />, accent: false }
  ];

  // Get unique languages
  const getUniqueLanguages = () => {
    if (!employeeData?.spokenLanguages) return [];

    const languagesMap = new Map();
    employeeData.spokenLanguages.forEach(lang => {
      if (lang?.spokenLanguage?.name) {
        languagesMap.set(lang.spokenLanguage.name, lang);
      }
    });

    return Array.from(languagesMap.values());
  };

  const uniqueLanguages = getUniqueLanguages();

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hourNum = parseInt(hours, 10);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum % 12 || 12;
    return `${displayHour}:${minutes} ${period}`;
  };

  const navigate = useNavigate();

  // Show loading only for critical data
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error only for critical employee data
  if (employeeError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {employeeError}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  if (!employeeData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          No employee data found
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Box sx={{
        minHeight: '100vh',
        py: 4,
        minWidth: '100%'
      }}>
        <Container maxWidth="xl">
          {/* Show non-critical errors as dismissible alerts */}
          {ratingsError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRatingsError(null)}>
              Unable to load ratings data. Displaying default values.
            </Alert>
          )}

          {clinicsError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setClinicsError(null)}>
              Unable to load clinic information.
            </Alert>
          )}

          {/* Profile Completion Card */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 4,
              background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
              border: '1px solid rgba(76, 175, 80, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
                {/* Completion Circle */}
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={120}
                    thickness={4}
                    sx={{ color: 'rgba(76, 175, 80, 0.1)', position: 'absolute' }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={completionData?.percentage}
                    size={120}
                    thickness={4}
                    sx={{
                      color: completionData?.percentage >= 80 ? '#4caf50' : completionData?.percentage >= 50 ? '#ff9800' : '#f44336',
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      }
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="h4" fontWeight="700" sx={{
                      color: completionData.percentage >= 80 ? '#4caf50' : completionData.percentage >= 50 ? '#ff9800' : '#f44336'
                    }}>
                      {completionData.percentage}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                      Complete
                    </Typography>
                  </Box>
                </Box>

                {/* Completion Details */}
                <Box sx={{ flex: 1, width: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <TrendingUp sx={{ color: '#4caf50', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="700" sx={{ color: '#1a1a1a' }}>
                      Profile Completion
                    </Typography>
                    <Chip
                      label={completionData.percentage >= 80 ? 'Excellent' : completionData.percentage >= 50 ? 'Good' : 'Needs Attention'}
                      size="small"
                      sx={{
                        backgroundColor: completionData.percentage >= 80 ? '#e8f5e8' : completionData.percentage >= 50 ? '#fff3e0' : '#ffebee',
                        color: completionData.percentage >= 80 ? '#4caf50' : completionData.percentage >= 50 ? '#ff9800' : '#f44336',
                        fontWeight: 600
                      }}
                    />
                  </Stack>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Complete your profile to unlock all features and improve your visibility to patients.
                  </Typography>

                  {/* Progress Items */}
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={3} flexWrap="wrap">
                      {completionData.allItems?.map((item, index) => (
                        <Stack key={index} direction="row" alignItems="center" spacing={1}>
                          {item.completed ? (
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />
                          ) : (
                            <RadioButtonUnchecked sx={{ color: '#bdbdbd', fontSize: 18 }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: item.completed ? '#4caf50' : '#757575',
                              fontWeight: item.completed ? 600 : 400,
                              textDecoration: item.completed ? 'none' : 'none'
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* Progress Bar */}
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={completionData.percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: completionData.percentage >= 80 ? '#4caf50' : completionData.percentage >= 50 ? '#ff9800' : '#f44336'
                        }
                      }}
                    />
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#4caf50',
                        '&:hover': { backgroundColor: '#45a049' },
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2
                      }}
                      onClick={() => navigate('/business-center/1')}
                    >
                      Complete Profile
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/business-center/2')}
                      sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                        '&:hover': { backgroundColor: '#388e3c' }
                      }}
                    >
                      Update Hospital/Lab Profile
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Background decoration */}
            <Box sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)',
              zIndex: 1
            }} />
          </Paper>

          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(46, 125, 50, 0.1)'
            }}
          >
            <div className="profile-section">
              {/* Profile Section */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        border: '3px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'white' }} />
                    </Box>
                  }
                >
                  <Avatar
                    src={employeeData.profilePicture || profilePicture}
                    sx={{
                      width: 140,
                      height: 140,
                      border: '4px solid white',
                      boxShadow: '0 8px 32px rgba(46, 125, 50, 0.2)'
                    }}
                  />
                </Badge>

                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <Typography variant="h4" fontWeight="700" sx={{ color: '#1a1a1a' }}>
                      {`${employeeData.title || ''} ${employeeData.firstName} ${employeeData.middleName ? employeeData.middleName + ' ' : ''}${employeeData.lastName}`}
                    </Typography>
                    {completionData?.percentage === 100 && (
                      <Chip
                        label="Verified"
                        icon={<Verified />}
                        size="medium"
                        className="green-bg green-primary"
                        sx={{
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#2e7d32' }
                        }}
                      />
                    )}
                  </Stack>

                  <Typography variant="h5" className="green-primary" sx={{ mb: 1, fontWeight: 600 }}>
                    {employeeData.userRoles?.[0]?.role?.name || 'Doctor'}
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Star className="orange-accent" sx={{ fontSize: 20 }} />
                      <Typography variant="body1" fontWeight="600">
                        {stats.averageRating}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({stats.totalReviews} reviews)
                      </Typography>
                      {ratingsError && (
                        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                          (Default)
                        </Typography>
                      )}
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      {employeeData.healthCareProvider?.name || 'National Hospital ABUJA'}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      {stats.totalConsultations} Consultations
                    </Typography>
                  </Stack>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, lineHeight: 1.6 }}>
                    Experienced {employeeData.userRoles?.[0]?.role?.name?.toLowerCase() || 'doctor'} specializing in {employeeData.department || 'general medicine'}.
                    {employeeData.description || ' Comprehensive care for patients.'}
                  </Typography>
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Stack spacing={2} alignItems="flex-end">
                <Stack direction="row" spacing={1}>

                  <IconButton
                    size="large"
                    onClick={() => navigate('/business-center/1')}
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.2)' }
                    }}
                  >
                    <Edit sx={{ color: '#4caf50' }} />
                  </IconButton>
                </Stack>

                <div style={{ textAlign: 'center' }}>
                  <Avatar
                    src={employeeData?.companyLogoUrl}
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '12px',
                      border: '2px dashed #81c784',
                      backgroundColor: 'transparent',
                      background: !employeeData?.companyLogoUrl
                        ? 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)'
                        : 'transparent',
                      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0px 4px 8px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    {!employeeData?.companyLogoUrl && (
                      <Business sx={{ fontSize: 48, color: '#66bb6a' }} />
                    )}
                  </Avatar>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Hospital Logo
                  </Typography>
                </div>
              </Stack>
            </div>
          </Paper>

          {/* Stats Cards */}
          <div className="stats-grid">
            {statsCards.map((stat, index) => (
              <Card
                key={index}
                elevation={0}
                sx={{
                  height: 120,
                  borderRadius: 3,
                  background: stat.accent
                    ? 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)'
                    : 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                  border: stat.accent ? '1px solid #ffcc80' : '1px solid #81c784',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: stat.accent
                      ? '0 12px 24px rgba(255, 152, 0, 0.3)'
                      : '0 12px 24px rgba(76, 175, 80, 0.3)'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ color: stat.accent ? '#ff9800' : '#4caf50', mb: 1 }}>
                    {React.cloneElement(stat.icon, { fontSize: 'large' })}
                  </Box>
                  <Typography variant="h5" fontWeight="700" sx={{ color: stat.accent ? '#ff9800' : '#4caf50', mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Main Content */}
          <div className="content-grid">
            {/* Left Column - Doctor Details */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(46, 125, 50, 0.1)',
                height: 'fit-content'
              }}
            >
              <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: '#1a1a1a' }}>
                Professional Information
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Schedule sx={{ color: '#4caf50', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      Working Hours
                    </Typography>
                  </Stack>
                  {employeeData.workSchedule && employeeData.workSchedule.length > 0 ? (
                    <Box sx={{ ml: 6 }}>
                      {(() => {
                        // Group schedules by day
                        const scheduleMap = {};
                        employeeData.workSchedule.forEach(schedule => {
                          const day = new Date(schedule.shiftDate).toLocaleDateString('en-US', { weekday: 'long' });
                          if (!scheduleMap[day]) {
                            scheduleMap[day] = [];
                          }
                          scheduleMap[day].push(schedule);
                        });

                        return Object.entries(scheduleMap).map(([day, schedules]) => (
                          <Typography key={day} variant="body1" color="text.secondary">
                            {day}: {schedules.map(s =>
                              `${formatTime(s.startTime)} - ${formatTime(s.endTime)}${s.notes ? ` (${s.notes})` : ''}`
                            ).join(', ')}
                          </Typography>
                        ));
                      })()}
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ ml: 6 }}>
                      Monday - Friday: 9:00 AM - 5:00 PM<br />
                      Saturday: 9:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Language sx={{ color: '#4caf50', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      Languages Spoken
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ ml: 6 }}>
                    {uniqueLanguages.length > 0 ? (
                      uniqueLanguages.map((langObj) => (
                        <Chip
                          key={langObj.id}
                          label={langObj.spokenLanguage.name}
                          size="small"
                          className="green-bg green-primary"
                          sx={{ fontWeight: 500, mb: 1 }}
                        />
                      ))
                    ) : (
                      ['English', 'French', 'Igbo', 'Yoruba'].map((lang) => (
                        <Chip
                          key={lang}
                          label={lang}
                          size="small"
                          className="green-bg green-primary"
                          sx={{ fontWeight: 500, mb: 1 }}
                        />
                      ))
                    )}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Email sx={{ color: '#4caf50', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      Email Address
                    </Typography>
                  </Stack>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 6 }}>
                    {employeeData.email || 'emberwynn94@nhahospital.com'}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Phone sx={{ color: '#4caf50', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      Phone Number
                    </Typography>
                  </Stack>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 6 }}>
                    {employeeData.phoneNumber || '+234-81-785-235'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Right Column - Associated Clinics */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(46, 125, 50, 0.1)',
                height: 'fit-content'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <LocalHospital sx={{ color: '#4caf50', fontSize: 24 }} />
                <Typography variant="h6" fontWeight="700" sx={{ color: '#1a1a1a' }}>
                  Associated Medical Centers
                </Typography>
              </Box>

              {clinicsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : clinicsError ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Unable to load clinic information at this time.
                </Alert>
              ) : clinics.length > 0 ? (
                <div className="clinic-list">
                  {clinics.map((clinic) => (
                    <div key={clinic.id} className="clinic-item">
                      <div className="clinic-header">
                        <div className="clinic-icon">
                          <LocalHospital />
                        </div>
                        <div className="clinic-info">
                          <h4>{clinic.name}</h4>
                          <p className="clinic-address">{clinic.healthCareProviderCategory?.name || 'Medical Center'}</p>
                          <p className="clinic-location">{clinic.location}</p>
                        </div>
                        <div className="clinic-status">
                          <span className={`status-badge ${clinic.status === 1 ? 'active' : 'inactive'}`}>
                            {clinic.status === 1 ? 'Available' : 'Closed'}
                          </span>
                        </div>
                      </div>
                      <div className="clinic-details">
                        <div className="doctor-info">
                          <strong>{`${employeeData.title || ''} ${employeeData.firstName} ${employeeData.lastName}`}</strong>
                          <span>{employeeData.userRoles?.[0]?.role?.name || 'Doctor'}</span>
                          <span>{clinic.phone || employeeData.phoneNumber || 'No phone provided'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No associated clinics found
                </Typography>
              )}
            </Paper>
          </div>
        </Container>
      </Box>
    </>
  );
};

export default BusinessCenter;